// Fills the official IRS Form W-9 (public/forms/fw9.pdf) with pdf-lib, draws the typed
// signature and date on top (the signature line is not an AcroForm field), flattens the
// result and returns the raw bytes. Ported from the Hich Transport driver portal's
// buildW9Pdf (onboard.html); field names were re-measured against this same PDF via
// pdf-lib's form.getFields() rather than assumed to still match.
//
// The SSN/EIN passed in here never leave the browser as anything other than pixels
// baked into these PDF bytes: nothing here logs them, and the caller is responsible for
// dropping them from React state once the upload completes.
import { loadPdfLib } from "./pdflib";

const F = (n) => `topmostSubform[0].Page1[0].${n}`;

// Line 3a classification checkboxes, in the order they appear on the form. Individual/sole
// proprietor is the only classification the source portal ever offered; LLC and Other are
// new to this page. The IRS form also has a one-letter LLC tax-classification box (f1_03,
// C/S/P) and a free-text Other description (f1_04); DriverW9.jsx now collects both and
// passes them in as llcClass / otherDescription so neither is left for an admin to fill in
// by hand.
const CLASSIFICATION_CHECKBOX = {
  individual: F("Boxes3a-b_ReadOrder[0].c1_1[0]"),
  llc: F("Boxes3a-b_ReadOrder[0].c1_1[5]"),
  other: F("Boxes3a-b_ReadOrder[0].c1_1[6]"),
};
const LLC_CLASS_FIELD = F("Boxes3a-b_ReadOrder[0].f1_03[0]");
const OTHER_DESC_FIELD = F("Boxes3a-b_ReadOrder[0].f1_04[0]");

function setText(form, knownFields, name, value) {
  if (!value) return;
  if (!knownFields.has(name)) {
    console.warn(`W-9 PDF: text field "${name}" not found on the current form, skipping`);
    return;
  }
  try {
    form.getTextField(name).setText(value);
  } catch (e) {
    console.warn(`W-9 PDF: could not fill "${name}"`, e);
  }
}

function check(form, knownFields, name) {
  if (!knownFields.has(name)) {
    console.warn(`W-9 PDF: checkbox "${name}" not found on the current form, skipping`);
    return;
  }
  try {
    form.getCheckBox(name).check();
  } catch (e) {
    console.warn(`W-9 PDF: could not check "${name}"`, e);
  }
}

export async function buildW9Pdf({
  name,
  businessName,
  classification,
  llcClass,
  otherDescription,
  addrStreet,
  addrCity,
  addrState,
  addrZip,
  ssn,
  ein,
  signature,
  signedOn,
}) {
  const PDFLib = await loadPdfLib();
  const bytes = await fetch("/forms/fw9.pdf").then((r) => r.arrayBuffer());
  const pdfDoc = await PDFLib.PDFDocument.load(bytes);
  const form = pdfDoc.getForm();
  const knownFields = new Set(form.getFields().map((f) => f.getName()));

  setText(form, knownFields, F("f1_01[0]"), name);
  setText(form, knownFields, F("f1_02[0]"), businessName);

  check(form, knownFields, CLASSIFICATION_CHECKBOX[classification] || CLASSIFICATION_CHECKBOX.individual);
  if (classification === "llc" && llcClass) setText(form, knownFields, LLC_CLASS_FIELD, llcClass.toUpperCase().slice(0, 1));
  if (classification === "other" && otherDescription) setText(form, knownFields, OTHER_DESC_FIELD, otherDescription);

  if (addrStreet) setText(form, knownFields, F("Address_ReadOrder[0].f1_07[0]"), addrStreet);
  const cityLine = [addrCity, addrState].filter(Boolean).join(", ") + (addrZip ? ` ${addrZip}` : "");
  if (cityLine.trim()) setText(form, knownFields, F("Address_ReadOrder[0].f1_08[0]"), cityLine.trim());

  if (ssn) {
    setText(form, knownFields, F("f1_11[0]"), ssn[0]);
    setText(form, knownFields, F("f1_12[0]"), ssn[1]);
    setText(form, knownFields, F("f1_13[0]"), ssn[2]);
  }
  if (ein) {
    setText(form, knownFields, F("f1_14[0]"), ein[0]);
    setText(form, knownFields, F("f1_15[0]"), ein[1]);
  }

  form.flatten();

  const page = pdfDoc.getPages()[0];
  const { rgb, StandardFonts } = PDFLib;
  const sigFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
  const dateFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  page.drawText(signature || "", { x: 125, y: 196, size: 11, font: sigFont, color: rgb(0.05, 0.05, 0.35) });
  const dateSource = signedOn ? new Date(`${signedOn}T00:00:00`) : new Date();
  const dateStr = dateSource.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  page.drawText(dateStr, { x: 405, y: 196, size: 10, font: dateFont, color: rgb(0, 0, 0) });

  return await pdfDoc.save();
}
